import { useState, useEffect } from "react";
import {
  Button,
  Grid,
  CircularProgress,
  Checkbox,
  Autocomplete,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import LoadingState from "../General/LoadingState";
import LegalFileStageFilter from "../General/LegalFileStageFilter";
import LegalFileTypeFilter from "../General/LegalFileTypeFilter";
import LegalFileStatusTaxFilter from "../General/LegalFileStatusTaxFilter";

import {
  getTypeOperation,
  getDependencies,
  getLocations,
} from "../../services/LegalFileService";

import { getLawyers } from "../../services/LawyerService";
import { downloadFilePost } from "../../services/ReportService";
import { useToast } from "../../context/ToastContext";

export default function LegalFileReport() {
  const { showToast } = useToast();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateType, setDateType] = useState("created_at");

  const [loading, setLoading] = useState(false);
  const [operationTypes, setOperationTypes] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [lawyers, setLawyers] = useState([]);

  const [selectedOperationTypes, setSelectedOperationTypes] = useState([]);
  const [selectedDependencies, setSelectedDependencies] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedLawyers, setSelectedLawyers] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatusTax, setSelectedStatusTax] = useState([]);

  const [loadingOptions, setLoadingOptions] = useState(true);
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    async function fetchFilters() {
      setLoadingOptions(true);
      try {
        const [resOperationTypes, resDependencies, resLocations, resLawyers] =
          await Promise.all([
            getTypeOperation(),
            getDependencies(),
            getLocations(),
            getLawyers(),
          ]);

        setOperationTypes(resOperationTypes.data.data || []);
        setDependencies(resDependencies.data.data || []);
        setLocations(resLocations.data.data || []);
        setLawyers(resLawyers.data.data || []);
      } catch (error) {
        console.error("Error loading filter options:", error);
        showToast("Error al cargar filtros.", "error")
      } finally {
        setLoadingOptions(false);
      }
    }

    fetchFilters();
  }, [showToast]);

  const handleGenerateReport = async () => {
    if (startDate && !endDate) {
      showToast("Debe completar la fecha 'Hasta' para continuar.", "error")
      return;
    }
    if (startDate && endDate && startDate.isAfter(endDate)) {
      showToast(
        "La fecha de inicio no puede ser posterior a la fecha de fin.",
      "error")
      return;
    }

    setLoading(true);

    try {
      const payload = {
        date_type: dateType,
        operation_types: selectedOperationTypes,
        dependencies: selectedDependencies,
        locations: selectedLocations,
        lawyers: selectedLawyers,
        legal_file_stage_ids: selectedStages.map((s) => s.id),
        legal_file_types: selectedTypes.map((t) => t.id),
        status_tax_ids: selectedStatusTax.map((s) => s.id),
      };

      if (startDate) payload.start_date = startDate.format("YYYY-MM-DD");
      if (endDate) payload.end_date = endDate.format("YYYY-MM-DD");

      const response = await downloadFilePost(
        "legal/file/report/export",
        payload,
      );

      const blobType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      const blob = new Blob([response.data], { type: blobType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const now = new Date();
      const formattedDate = now
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-")
        .replace("T", "_");

      const filename = `reportes_expedientes_${formattedDate}.xlsx`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      showToast("Ocurrió un error al generar el reporte.", "error")
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions) {
    return <LoadingState text="Cargando filtros..." />;
  }

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>
        <GlassCard>
          <CardHeader
            icon={<AssignmentIcon />}
            title="Reportes de Expedientes"
            description="Genera un reporte de expedientes cargados en el sistema."
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <TextField
                select
                fullWidth
                label="Tipo de Fecha"
                value={dateType}
                onChange={(e) => setDateType(e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="created_at">Fecha de creación</option>
                <option value="payment_date">Fecha de operacion</option>
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
              <DatePicker
                label="Desde"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
              <DatePicker
                label="Hasta"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <LegalFileStageFilter
                value={selectedStages}
                onChange={setSelectedStages}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <LegalFileTypeFilter
                value={selectedTypes}
                onChange={setSelectedTypes}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <LegalFileStatusTaxFilter
                value={selectedStatusTax}
                onChange={setSelectedStatusTax}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Autocomplete
                multiple
                options={operationTypes}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name || option}
                value={selectedOperationTypes}
                onChange={(event, newValue) =>
                  setSelectedOperationTypes(newValue)
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      checked={selected}
                    />
                    {option.name || option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tipos de Operaciones"
                    placeholder="Seleccionar..."
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Autocomplete
                multiple
                options={dependencies}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name || option}
                value={selectedDependencies}
                onChange={(event, newValue) =>
                  setSelectedDependencies(newValue)
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      checked={selected}
                    />
                    {option.name || option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Dependencias"
                    placeholder="Seleccionar..."
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Autocomplete
                multiple
                options={locations}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name || option}
                value={selectedLocations}
                onChange={(event, newValue) => setSelectedLocations(newValue)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      checked={selected}
                    />
                    {option.name || option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ubicaciones"
                    placeholder="Seleccionar..."
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Autocomplete
                multiple
                options={lawyers}
                disableCloseOnSelect
                getOptionLabel={(option) =>
                  `${option.name} ${option.lastname}` || option
                }
                value={selectedLawyers}
                onChange={(event, newValue) => setSelectedLawyers(newValue)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      checked={selected}
                    />
                    {`${option.name} ${option.lastname}` || option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Abogados"
                    placeholder="Seleccionar..."
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleGenerateReport}
               
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Generar Reporte"
                )}
              </Button>
            </Grid>
          </Grid>
        </GlassCard>
      </Grid>
    </Grid>
  );
}
